import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const suggestedQuestions = [
  'What should I do first?',
  'How do I get my visa?',
  'Which neighborhood fits me?',
];

const AIChatFloat = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  const sendMessage = async (text) => {
    const messageText = text || input.trim();
    if (!messageText || loading) return;
    
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: messageText }]);
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/blueprint/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: user?.email,
          message: messageText,
        }),
      });
      
      const data = await res.json();
      
      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', text: data.response }]);
      } else if (data.error) {
        // Escalation message if AI can't answer
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          text: "I'll connect you with the Mundalo team - email hello@mundalo.com or expect a follow-up shortly."
        }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        text: "I'm having trouble connecting right now. Please email hello@mundalo.com and we'll help you right away."
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Chat bubble button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 md:bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#2CA8A0] text-white flex items-center justify-center shadow-lg hover:bg-[#1A5C6E] transition-all hover:scale-105"
        aria-label="AI Chat"
        data-testid="ai-chat-float-btn"
      >
        {isOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      {/* Chat window */}
      {isOpen && (
        <div 
          className="fixed bottom-40 md:bottom-24 right-6 z-50 w-[calc(100vw-48px)] max-w-[380px] h-[500px] max-h-[70vh] bg-[#F9F6EF] border border-[#EFE6CF] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="p-4 border-b border-[#EFE6CF] flex items-center gap-3 bg-[#FCF8EE]">
            <div className="w-8 h-8 rounded-full bg-[#2CA8A0]/15 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-[#1A5C6E]" />
            </div>
            <div>
              <h3 className="text-[#0A1F2E] text-sm font-medium">Mundalo Assistant</h3>
              <p className="text-[#0A1F2E]/55 text-xs">Ask anything about your move</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F9F6EF]">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-10 h-10 text-[#1A5C6E]/70 mx-auto mb-4" />
                <p className="text-[#0A1F2E]/65 text-sm mb-4">How can I help with your relocation?</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {suggestedQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="text-xs px-3 py-1.5 bg-white border border-[#EFE6CF] text-[#0A1F2E] hover:bg-[#FCF8EE] hover:border-[#2CA8A0]/40 transition rounded-full"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-[#2CA8A0] text-white rounded-br-sm'
                          : 'bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-bl-sm'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-[#EFE6CF] px-4 py-2 rounded-2xl rounded-bl-sm">
                      <Loader2 className="w-4 h-4 text-[#2CA8A0] animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[#EFE6CF] bg-[#FCF8EE]">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Ask about your relocation..."
                className="flex-1 bg-white border border-[#EFE6CF] text-[#0A1F2E] placeholder:text-[#0A1F2E]/40 text-sm px-3 py-2 rounded-lg focus:border-[#2CA8A0] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/20 transition"
                data-testid="ai-chat-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                className="bg-[#2CA8A0] text-white p-2 rounded-lg hover:bg-[#1A5C6E] transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="ai-chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatFloat;
